import React, { useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography } from '@mui/material';
import aiAvatarImg from '../../assets/brand-icons/ai_avatar.png';

const AVA = `<img src="${aiAvatarImg}" style="width:26px;height:26px;border-radius:50%;object-fit:cover;flex-shrink:0" alt="AI">`;

// 建立 bot 訊息（含 avatar + "AI 助理" 標籤）
const botBubble = (text) =>
  `<div style="display:flex;gap:8px">
    ${AVA}
    <div><div style="font-size:10px;color:#9ca3af;margin-bottom:2px">AI 助理</div><div style="background:#f3f4f6;border-radius:12px 12px 12px 2px;padding:8px 12px;font-size:14px;color:#374151;line-height:1.5">${text}</div></div>
  </div>`;

const userBubble = (text) =>
  `<div style="display:flex;justify-content:flex-end"><div style="background:#3664D1;color:#fff;border-radius:12px 12px 2px 12px;padding:8px 12px;font-size:14px">${text}</div></div>`;

const addMsg = (container, html, delay) =>
  new Promise((resolve) => {
    setTimeout(() => {
      const div = document.createElement('div');
      div.className = 'cf-msg';
      div.innerHTML = html;
      container.appendChild(div);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => div.classList.add('cf-msg-show'));
      });
      container.scrollTop = container.scrollHeight;
      resolve();
    }, delay);
  });

const showTyping = (container) => {
  const d = document.createElement('div');
  d.id = 'cfTyping';
  d.className = 'cf-msg cf-msg-show';
  d.innerHTML = `<div style="display:flex;gap:8px;align-items:center">
    ${AVA}
    <div style="background:#f3f4f6;border-radius:12px;padding:8px 14px;display:flex;gap:6px">
      <span class="cf-dot"></span><span class="cf-dot" style="animation-delay:.2s"></span><span class="cf-dot" style="animation-delay:.4s"></span>
    </div>
  </div>`;
  container.appendChild(d);
  container.scrollTop = container.scrollHeight;
};

const removeTyping = () => {
  const el = document.getElementById('cfTyping');
  if (el) el.remove();
};

export const FeatureChatFlow = () => {
  const { t, i18n } = useTranslation('landing');
  const sectionRef = useRef(null);
  const playedRef = useRef(false);
  const cancelRef = useRef(false);
  const langRef = useRef(i18n.language);
  const d = 'featureChatFlow.demo';

  const replay = useCallback(async () => {
    const avatar = document.getElementById('cfAvatar');
    const widget = document.getElementById('cfWidget');
    const msgs = document.getElementById('cfMsgs');
    const ripple = document.getElementById('cfRipple');
    if (!avatar || !widget || !msgs || !ripple) return;

    const wait = (ms) => new Promise((r) => setTimeout(r, ms));

    // 重設
    cancelRef.current = false;
    avatar.style.display = 'none';
    widget.style.display = 'none';
    widget.classList.remove('cf-widget-enter');
    msgs.innerHTML = '';
    ripple.style.display = 'none';
    ripple.classList.remove('cf-ripple-go');

    // 1. 顯示 avatar
    await wait(600);
    if (cancelRef.current) return;
    avatar.style.display = 'block';

    // 2. Ripple 點擊效果
    await wait(800);
    if (cancelRef.current) return;
    ripple.style.display = 'block';
    ripple.style.bottom = '36px';
    ripple.style.right = '36px';
    ripple.className = 'cf-ripple';
    void ripple.offsetWidth;
    ripple.className = 'cf-ripple cf-ripple-go';

    // 3. 開啟 widget
    await wait(300);
    if (cancelRef.current) return;
    avatar.style.display = 'none';
    ripple.style.display = 'none';
    widget.style.display = 'block';
    widget.classList.remove('cf-widget-enter');
    void widget.offsetWidth;
    widget.classList.add('cf-widget-enter');

    // 4. Typing → 問服務
    await wait(400);
    if (cancelRef.current) return;
    showTyping(msgs);
    await wait(900);
    if (cancelRef.current) return;
    removeTyping();
    await addMsg(msgs, botBubble(t(d + '.askService')), 0);

    // 5. 按鈕（機場接送、環島租車、短期租車）
    await addMsg(msgs, `<div style="display:flex;flex-wrap:wrap;gap:6px;padding-left:34px">
      <span class="cf-btn" style="border:1.5px solid #3664D1;color:#3664D1;border-radius:20px;padding:5px 14px;font-size:13px;font-weight:500;background:#fff">${t(d + '.btn2')}</span>
      <span class="cf-btn" style="border:1.5px solid #3664D1;color:#3664D1;border-radius:20px;padding:5px 14px;font-size:13px;font-weight:500;background:#fff">${t(d + '.btn1')}</span>
      <span class="cf-btn" style="border:1.5px solid #3664D1;color:#3664D1;border-radius:20px;padding:5px 14px;font-size:13px;font-weight:500;background:#fff">${t(d + '.btn3')}</span>
    </div>`, 500);

    // 6. 使用者點選「環島租車」— highlight
    await wait(1000);
    if (cancelRef.current) return;
    const btns = msgs.querySelectorAll('.cf-btn');
    if (btns[1]) { btns[1].style.background = '#3664D1'; btns[1].style.color = '#fff'; }

    // 7. 使用者訊息
    await wait(300);
    if (cancelRef.current) return;
    await addMsg(msgs, userBubble(t(d + '.userChoice')), 0);

    // 8. Typing → 問日期
    await wait(500);
    if (cancelRef.current) return;
    showTyping(msgs);
    await wait(800);
    if (cancelRef.current) return;
    removeTyping();
    await addMsg(msgs, botBubble(t(d + '.askDate')), 0);

    // 9. 日期選擇按鈕（含日曆 icon）
    await addMsg(msgs, `<div style="padding-left:34px">
      <span style="border:1.5px solid #3664D1;color:#3664D1;border-radius:20px;padding:5px 14px;font-size:13px;font-weight:500;background:#fff;display:inline-flex;align-items:center;gap:4px">
        <svg width="13" height="13" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
        ${t(d + '.askDate')}
      </span>
    </div>`, 500);

    // 10. 使用者選日期
    await wait(1200);
    if (cancelRef.current) return;
    await addMsg(msgs, userBubble(t(d + '.userDate')), 0);

    // 11. Typing → 確認
    await wait(400);
    if (cancelRef.current) return;
    showTyping(msgs);
    await wait(800);
    if (cancelRef.current) return;
    removeTyping();
    await addMsg(msgs, botBubble(t(d + '.confirm')), 0);

    // 12. 確認/重選按鈕
    await addMsg(msgs, `<div style="display:flex;gap:6px;padding-left:34px">
      <span style="background:#10b981;color:#fff;border-radius:20px;padding:5px 14px;font-size:13px;font-weight:600">${t(d + '.day2')}</span>
      <span style="border:1.5px solid #d1d5db;color:#6b7280;border-radius:20px;padding:5px 14px;font-size:13px;font-weight:500;background:#fff">${t(d + '.day1')}</span>
    </div>`, 500);

    // 13. 等待後重播
    await wait(5000);
    if (!cancelRef.current) replay();
  }, [t, d]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && !playedRef.current) {
            playedRef.current = true;
            replay();
          }
        });
      },
      { threshold: 0.3 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => {
      cancelRef.current = true;
      observer.disconnect();
    };
  }, [replay]);

  // 語言切換時重新播放動畫
  useEffect(() => {
    if (langRef.current !== i18n.language && playedRef.current) {
      langRef.current = i18n.language;
      cancelRef.current = true;
      setTimeout(() => {
        cancelRef.current = false;
        replay();
      }, 100);
    }
  }, [i18n.language, replay]);

  return (
    <Box component="section" ref={sectionRef} sx={{ py: { xs: '60px', md: '100px' }, background: '#fff', overflow: 'hidden' }}>
      <Box sx={{ maxWidth: 1440, mx: 'auto', px: { xs: '20px', md: '40px' } }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '35% 62%' }, gap: '3%', alignItems: 'center' }}>
          {/* Text */}
          <Box className="omni-reveal">
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: 16, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: '#06C755', mb: 2 }}>
              <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>
              {t('featureChatFlow.label')}
            </Box>
            <Typography variant="h3" sx={{ fontSize: { xs: '1.6rem', md: 'clamp(1.6rem, 3vw, 2.2rem)' }, fontWeight: 800, lineHeight: 1.3, mb: 2, letterSpacing: '-0.3px', color: '#0f1729' }}>
              {t('featureChatFlow.title')}
            </Typography>
            <Typography sx={{ fontSize: 19, color: '#374151', lineHeight: 1.8, mb: '28px' }}>
              {t('featureChatFlow.desc')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {['bullet1', 'bullet2', 'bullet3'].map((key) => (
                <Box key={key} sx={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                  <Box sx={{ width: 24, height: 24, borderRadius: '50%', background: '#06C755', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, mt: '2px', fontSize: 15, fontWeight: 700, color: '#fff' }}>&#10003;</Box>
                  <Typography sx={{ fontSize: 18, color: '#374151', lineHeight: 1.5 }}>{t(`featureChatFlow.${key}`)}</Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Animated Demo */}
          <Box className="omni-reveal omni-reveal-delay-1">
            <Box id="cfDemo" sx={{
              position: 'relative', width: '100%', maxWidth: 520, height: 480,
              background: 'linear-gradient(135deg, #EBF5FF, #E8EEFF)',
              borderRadius: '16px', boxShadow: '0 20px 60px rgba(0,0,0,0.1)',
              overflow: 'hidden', mx: 'auto',
            }}>
              {/* Fake browser */}
              <Box sx={{ p: '20px' }}>
                <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ width: 12, height: 12, borderRadius: '50%', background: '#F87171' }} />
                  <Box sx={{ width: 12, height: 12, borderRadius: '50%', background: '#FBBF24' }} />
                  <Box sx={{ width: 12, height: 12, borderRadius: '50%', background: '#34D399' }} />
                  <Box sx={{ flex: 1, background: '#fff', borderRadius: 99, py: '4px', px: '14px', ml: 1, fontSize: 12, color: '#9ca3af' }}>www.your-website.com</Box>
                </Box>
                <Box sx={{ background: '#fff', borderRadius: '10px', p: 2, mb: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                  <Box sx={{ height: 14, background: '#e5e7eb', borderRadius: '6px', width: '60%', mb: 1 }} />
                  <Box sx={{ height: 10, background: '#f3f4f6', borderRadius: '4px', mb: '6px' }} />
                  <Box sx={{ height: 10, background: '#f3f4f6', borderRadius: '4px', width: '75%' }} />
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>
                  {[1,2,3].map(i => (
                    <Box key={i} sx={{ background: '#fff', borderRadius: '8px', p: '10px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                      <Box sx={{ height: 8, background: '#e5e7eb', borderRadius: '4px', mb: '6px' }} />
                      <Box sx={{ height: 8, background: '#f3f4f6', borderRadius: '4px', width: '60%' }} />
                    </Box>
                  ))}
                </Box>
              </Box>

              {/* Chat avatar */}
              <Box id="cfAvatar" sx={{ position: 'absolute', bottom: 20, right: 20, display: 'none', cursor: 'pointer' }}>
                <img className="cf-pulse" src={aiAvatarImg} alt="AI" style={{ width: 56, height: 56, borderRadius: '50%', objectFit: 'cover', border: '3px solid #fff', boxShadow: '0 4px 16px rgba(54,100,209,0.4)' }} />
                <Box sx={{ position: 'absolute', top: -4, right: -4, width: 20, height: 20, background: '#EF4444', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 10, fontWeight: 700, border: '2px solid #fff' }}>1</Box>
              </Box>

              {/* Ripple */}
              <Box id="cfRipple" className="cf-ripple" sx={{ display: 'none' }} />

              {/* Chat widget */}
              <Box id="cfWidget" sx={{
                position: 'absolute', bottom: 16, right: 16, width: 320,
                background: '#fff', borderRadius: '16px',
                boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
                overflow: 'hidden', display: 'none',
              }}>
                <Box sx={{ background: '#3664D1', color: '#fff', p: '12px 16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <img src={aiAvatarImg} alt="AI" style={{ width: 34, height: 34, borderRadius: '50%', objectFit: 'cover', border: '2px solid rgba(255,255,255,0.3)' }} />
                  <Box>
                    <Box sx={{ fontWeight: 600, fontSize: 14 }}>{t(d + '.shopName')}</Box>
                    <Box sx={{ fontSize: 11, opacity: 0.7 }}>{t(d + '.shopSub')}</Box>
                  </Box>
                  <Box sx={{ ml: 'auto', opacity: 0.5, fontSize: 14 }}>✕</Box>
                </Box>
                <Box id="cfMsgs" sx={{
                  p: '14px', minHeight: 260, maxHeight: 300, overflowY: 'auto',
                  display: 'flex', flexDirection: 'column', gap: '10px',
                  background: '#fafbfc',
                }} />
                <Box sx={{ borderTop: '1px solid #e5e7eb', p: '10px 14px', display: 'flex', alignItems: 'center', gap: '8px', background: '#f9fafb' }}>
                  <Box sx={{ flex: 1, border: '1px solid #e5e7eb', borderRadius: 99, py: '6px', px: '14px', fontSize: 13, color: '#9ca3af', background: '#fff' }}>
                    {t(d + '.inputPlaceholder')}
                  </Box>
                  <Box sx={{
                    width: 30, height: 30, borderRadius: '50%', background: '#3664D1',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <svg width="14" height="14" fill="#fff" viewBox="0 0 20 20"><path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z"/></svg>
                  </Box>
                </Box>
                <Box sx={{ textAlign: 'center', fontSize: 10, color: '#d1d5db', py: '3px', borderTop: '1px solid #f3f4f6' }}>Powered by Rebaz AI</Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
